var React = require('react');
var { useState, useEffect, useCallback } = require('react');
var { useTranslation } = require('react-i18next');
var { useLocation } = require('react-router-dom');
var SupportFormFields = require('./SupportFormFields');
var { useSupportForm } = require('./useSupportForm');
require('./SupportWidget.css');

/** Zendesk Web Widget launcher icon (? in circle), viewBox 0 0 20 20 */
function ZendeskLauncherIcon() {
  return (
    <svg
      className='support-widget-launcher__icon'
      xmlns='http://www.w3.org/2000/svg'
      width='20'
      height='20'
      viewBox='0 0 20 20'
      xmlSpace='preserve'
      aria-hidden='true'
    >
      <g>
        <g>
          <path
            fill='currentColor'
            d='M11,12.3V13c0,0-1.8,0-2,0v-0.6c0-0.6,0.1-1.4,0.8-2.1c0.7-0.7,1.6-1.2,1.6-2.1c0-0.9-0.7-1.4-1.4-1.4 c-1.3,0-1.4,1.4-1.5,1.7H6.6C6.6,7.1,7.2,5,10,5c2.4,0,3.4,1.6,3.4,3C13.4,10.4,11,10.8,11,12.3z'
          />
          <circle fill='currentColor' cx='10' cy='15' r='1' />
        </g>
        <path
          fill='currentColor'
          d='M10,2c4.4,0,8,3.6,8,8s-3.6,8-8,8s-8-3.6-8-8S5.6,2,10,2 M10,0C4.5,0,0,4.5,0,10s4.5,10,10,10s10-4.5,10-10S15.5,0,10,0 L10,0z'
        />
      </g>
    </svg>
  );
}

function MinimizeIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 16 16'
      aria-hidden='true'
    >
      <path
        stroke='currentColor'
        strokeLinecap='round'
        strokeWidth='2'
        d='M3 8h10'
      />
    </svg>
  );
}

function SupportWidget() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const form = useSupportForm();

  const hiddenRoutes = ['/register', '/confirm', '/forgot', '/Support'];
  const isHidden = hiddenRoutes.some((path) =>
    location.pathname.startsWith(path),
  );

  const closePanel = useCallback(() => {
    setIsOpen(false);
    form.resetStatus();
  }, [form.resetStatus]);

  const openPanel = useCallback(() => {
    setIsOpen(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closePanel();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closePanel]);

  useEffect(() => {
    closePanel();
  }, [location.pathname, closePanel]);

  if (isHidden) {
    return null;
  }

  return (
    <div
      className={`support-widget-root${isOpen ? ' support-widget-root--open' : ''}`}
      aria-live='polite'
    >
      <div
        id='support-widget-panel'
        className={`support-widget-panel${isOpen ? ' support-widget-panel--open' : ''}`}
        role='dialog'
        aria-modal='true'
        aria-labelledby='support-widget-title'
        aria-hidden={!isOpen}
      >
        <header
          className='support-widget-panel__header'
          tabIndex={-1}
          data-testid='widget-header-view'
        >
          <div className='support-widget-panel__header-row'>
            <span
              className='support-widget-panel__header-spacer'
              aria-hidden='true'
            />
            <h1
              id='support-widget-title'
              className='support-widget-panel__title'
              data-testid='widget-title'
            >
              {t('support_leave_message')}
            </h1>
            <button
              type='button'
              className='support-widget-panel__close'
              onClick={closePanel}
              aria-label={t('support_minimize_widget')}
            >
              <MinimizeIcon />
            </button>
          </div>
        </header>

        <SupportFormFields
          compact
          messageId='support-widget-message'
          t={form.t}
          isAuthenticated={form.isAuthenticated}
          displayName={form.displayName}
          email={form.email}
          message={form.message}
          attachments={form.attachments}
          loading={form.loading}
          result={form.result}
          errorDetail={form.errorDetail}
          canSubmit={form.canSubmit}
          canAddMoreFiles={form.canAddMoreFiles}
          fileInputRef={form.fileInputRef}
          maxAttachments={form.maxAttachments}
          onNameChange={form.handleNameChange}
          onEmailChange={form.handleEmailChange}
          onMessageChange={form.handleMessageChange}
          onSubmit={form.handleSubmit}
          onAddFiles={form.handleAddFiles}
          onFileInputChange={form.handleFileInputChange}
          onRemoveAttachment={form.handleRemoveAttachment}
          onOpenFilePicker={form.openFilePicker}
        />
      </div>

      {!isOpen ? (
        <button
          type='button'
          data-testid='launcher'
          className='support-widget-launcher'
          onClick={openPanel}
          aria-label={t('support')}
          aria-expanded={false}
          aria-controls='support-widget-panel'
          title='Opens a widget where you can find more information'
        >
          <span className='support-widget-launcher__icon-wrap'>
            <ZendeskLauncherIcon />
          </span>
          <span
            className='support-widget-launcher__label'
            data-testid='launcher-label'
          >
            {t('support')}
          </span>
        </button>
      ) : null}
    </div>
  );
}

module.exports = SupportWidget;
